import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin, interval, Observable } from 'rxjs';
import { switchMap, catchError, map, filter, withLatestFrom, tap, first, takeWhile } from 'rxjs/operators';
import { isNil } from 'lodash-es';

import { DialogComponent, BeachyDialogData } from '../../ui/dialog/dialog.component';
import { OrderTimeoutActions, OrderActions, OrderTransactionActions } from '../actions';
import { OrderService } from '../order.service';
import { OrderActionService, RoutingActionService, DestinationActionService, DateOfStayActionService, OwnerActionService } from '../core-action-services';
import { EnvironmentService } from '../environment.service';
import { createOrderPayload, hasItems } from 'src/objects/order';
import { ConfirmationDialogComponent } from 'src/app/ui/confirmation-dialog/confirmation-dialog.component';



@Injectable()
export class OrderTimeoutEffects {
  private actions$ = inject(Actions);
  private dialog = inject(MatDialog);
  private orderService = inject(OrderService);
  private orderActionsService = inject(OrderActionService);
  private destinationActionService = inject(DestinationActionService);
  private dateOfStayActionService = inject(DateOfStayActionService);
  private routingActionService = inject(RoutingActionService);
  private environmentService = inject(EnvironmentService);
  private ownerActionService = inject(OwnerActionService);

  resetTimerValue$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.resetTimeout),
      map(({ order }) => OrderTimeoutActions.updateTimeout({
        order, timeout: this.minutesUntilOrderGetsClearedOut * 60,
      })),
    ),
  );
  /**
   * Start counting down
   */
  startCountdownTimer$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.resetTimeoutSuccess),
      switchMap(({ order }) => {
        if (!hasItems(order)) {
          return of(OrderTimeoutActions.updateTimeout({ order, timeout: 0 }));
        }

        return this.intervalFn(1000)
          .pipe(
            withLatestFrom(this.orderActionsService.getOrderTimeout()),
            takeWhile(([_counter, timeoutValue]) => timeoutValue > 0),
            switchMap(([counter, _timeoutValue]) => {
              const timeout = this.minutesUntilOrderGetsClearedOut * 60 - counter;
              const actionsToDispatch = [
                OrderTimeoutActions.updateTimeout({
                  order,
                  timeout,
                }),
              ];

              if (timeout === (this.minutesUntilOrderGetsClearedOut - this.minutesUntilWarningAppears) * 60) {
                return [
                  ...actionsToDispatch,
                  OrderTimeoutActions.timeoutWarning({ order }),
                ];
              }

              if (timeout === 0) {
                return [
                  ...actionsToDispatch,
                  OrderTimeoutActions.timeoutError({ order }),
                ];
              }

              return actionsToDispatch;
            }),
          );
      }),
    ),
  );

  /**
   * Show warning message
   */
  showWarningMessage$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.timeoutWarning),
      switchMap(() => {
        if (!isNil(this.dialogRef)) {
          this.dialogRef.close();
        }

        const differenceInMinutes = this.minutesUntilOrderGetsClearedOut - this.minutesUntilWarningAppears;
        const humanReadableDifferenceInMinutes = differenceInMinutes === 1 ? 'a minute' : `${differenceInMinutes} minutes`;

        this.dialogRef = this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Are you still shopping?',
              message: `If not, your cart will be cleared in ${humanReadableDifferenceInMinutes}`,
              yesLabel: 'Yes',
              noLabel: 'Ignore',
            },
          });
        return this.dialogRef.afterClosed();
      }),
      filter(result => !!result),
      withLatestFrom(this.orderActionsService.getOrder().pipe(filter(x => !!x))),
      map(([_result, order]) => OrderTimeoutActions.resetTimeout({ order })),
    ),
  );

  /**
   * Show error message
   *
   * TODO: rewrite when we have router store implemented
   * dispatch: false until then
   */
  showErrorMessage$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.timeoutError),
      switchMap(() => {
        if (!isNil(this.dialogRef)) {
          this.dialogRef.close();
        }

        this.dialogRef = this.dialog
          .open(DialogComponent, {
            panelClass: 'error-modal',
            data: {
              error: new Error('Your cart has been cleared due to inactivity.'),
            } as BeachyDialogData,
          });

        return this.dialogRef
          .afterClosed()
          .pipe(
            switchMap(() => forkJoin([
              this.orderActionsService.getOrder().pipe(filter(x => !!x), first()),
              this.dateOfStayActionService.getDateOfStay().pipe(filter(x => !!x), first()),
              this.destinationActionService.getDestination().pipe(filter(x => !!x), first()),
            ])),
            tap(([order, dateOfStay, destination]) => {
              this.routingActionService.navigateToProducts({ dateOfStay, destination, order });
            }),
          );
      }),
    ),
    {
      dispatch: false,
    },
  );

  /**
   * Clear out the order and ask for a new one when timeout error
   * is fired
   */
  clearOutTheOrder$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.timeoutError),
      switchMap(({ order }) => this.ownerActionService.getStorefront().pipe(first(), map(storefront => ({order, storefront})))),
      map(({ order, storefront }) => createOrderPayload({ storefront, order })),
      switchMap(payload => [OrderActions.createOrder({payload})]),
    ),
  );

  /**
   * TODO: Not sure if this is efficient enough, as
   * in the store we might want to override the order that
   * has already been overridden a while ago by one of these
   * 'success' actions
   */
  resetTimerByOrderUpdateAction$ = createEffect(() => this.actions$
    .pipe(
      ofType(
        OrderActions.addItemSuccess,
        OrderActions.removeItemSuccess,
      ),
      map(({ order }) => OrderTimeoutActions.resetTimeout({ order })),
    ),
  );

  resetTimerByOrderLoadAction$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderActions.loadOrderSuccess),
      filter(({ order }) => hasItems(order)),
      map(({ order }) => OrderTimeoutActions.resetTimeout({ order })),
    ),
  );

  resetTimerWhenCheckoutBegins$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.beginCheckout),
      withLatestFrom(this.orderActionsService.getOrder().pipe(filter(x => !!x))),
      map(([_v, order]) => OrderTimeoutActions.resetTimeout({ order })),
    ),
  );

  resetTimerByUser$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.resetTimeout),
      switchMap(({ order }) => this.orderService
        .resetOrderTimeout({ order })
        .pipe(
          map(updatedOrder => OrderTimeoutActions.resetTimeoutSuccess({ order: updatedOrder })),
          catchError(error => of(OrderTimeoutActions.resetTimeoutFailure({ error, order }))),
        ),
      ),
    ),
  );

  resetTimerIfTransactionFails$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.checkoutFailure),
      withLatestFrom(this.orderActionsService.getOrder().pipe(filter(x => !!x))),
      map(([_action, order]) => OrderTimeoutActions.resetTimeout({ order })),
    ),
  );

  /**
   * Setting timeout to 0 stops timer interval
   */
  stopTimerWhenTransactionStarts$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.checkout),
      map(({ order }) => OrderTimeoutActions.updateTimeout({ order, timeout: 0 })),
    ),
  );

  /**
   * An assumption: If we are unable to reset the timeout (touch an order)
   * we should ask for a new order instance
   */
  resetTimeoutFailure$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTimeoutActions.resetTimeoutFailure),
      switchMap(({ order }) => this.ownerActionService.getStorefront().pipe(first(), map(storefront => ({order, storefront})))),
      map(({order, storefront}) => createOrderPayload({ storefront, order })),
      map(payload => OrderActions.createOrder({ payload })),
    ),
  );

  private dialogRef: MatDialogRef<ConfirmationDialogComponent | DialogComponent> | null = null;
  private minutesUntilWarningAppears: number;
  private minutesUntilOrderGetsClearedOut: number; // after warning appears

  constructor() {
    const { orderTimeoutInMinutes } = this.environmentService.resolve();

    this.minutesUntilWarningAppears = orderTimeoutInMinutes?.warning ?? 5;
    this.minutesUntilOrderGetsClearedOut = orderTimeoutInMinutes?.error ?? 10;
  }

  /**
   * Added just for testing purposes. It's way easier
   * to mock public method of a class and user marbles
   * than to deal with all fakeAsync/timer-tick stuff.
   */
  intervalFn = (timeInMs: number): Observable<number> => interval(timeInMs);
}

